import { useState, useRef, useCallback, useEffect } from "react";
import { flushSync } from "react-dom";

export default function usePipWindow() {
  const [isPipOpen, setIsPipOpen] = useState(false);
  const [portalRoot, setPortalRoot] = useState(null);
  const pipWindowRef = useRef(null);

  const cloneStyles = useCallback(async (targetDoc) => {
    const origin = window.location.origin;

    // Set base href so relative URLs resolve correctly in PiP document
    const base = targetDoc.createElement("base");
    base.href = origin + "/";
    targetDoc.head.appendChild(base);

    // Clone <style> tags first (sync - contains Tailwind/CRA compiled CSS)
    const styleTags = document.querySelectorAll("style");
    styleTags.forEach((style) => {
      const clone = targetDoc.createElement("style");
      clone.textContent = style.textContent;
      targetDoc.head.appendChild(clone);
    });

    // Clone and FETCH <link> stylesheets as inline <style> for reliable loading in PiP
    const linkPromises = [];
    document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
      const href = link.href ? new URL(link.href, document.baseURI).href : link.getAttribute("href");
      if (!href || href.startsWith("blob:")) return;
      linkPromises.push(
        fetch(href)
          .then((r) => r.text())
          .then((css) => {
            const style = targetDoc.createElement("style");
            style.textContent = css;
            targetDoc.head.appendChild(style);
            return { ok: true, href, len: css.length };
          })
          .catch((err) => {
            const clone = targetDoc.createElement("link");
            clone.rel = "stylesheet";
            clone.href = href;
            targetDoc.head.appendChild(clone);
            return { ok: false, href, err: String(err?.message||err) };
          })
      );
    });

    const linkResults = await Promise.all(linkPromises);

    try {
      if (document.adoptedStyleSheets?.length) {
        targetDoc.adoptedStyleSheets = [...document.adoptedStyleSheets];
      }
    } catch {
      // adoptedStyleSheets may not be writable
    }
  }, []);

  const openPip = useCallback(async (opts = {}) => {
    const { width = 380, height = 540 } = opts;

    if (typeof window.documentPictureInPicture === "undefined") {
      throw new Error("Document Picture-in-Picture API is not supported in this browser.");
    }

    if (pipWindowRef.current && !pipWindowRef.current.closed) {
      pipWindowRef.current.focus();
      return pipWindowRef.current;
    }

    const pipWin = await window.documentPictureInPicture.requestWindow({ width, height });
    pipWindowRef.current = pipWin;

    await cloneStyles(pipWin.document);

    // Base styles for the PiP document: full height so #pip-root can fill it
    const html = pipWin.document.documentElement;
    const body = pipWin.document.body;
    html.style.height = "100%";
    html.style.margin = "0";
    body.style.height = "100%";
    body.style.margin = "0";
    body.style.padding = "0";
    body.style.fontFamily =
      "'Inter', -apple-system, BlinkMacSystemFont, system-ui, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
    body.style.background = "hsl(40 14% 96%)";
    body.style.overflow = "hidden";

    const root = pipWin.document.createElement("div");
    root.id = "pip-root";
    root.style.cssText = "width:100%;height:100%;min-height:100%;overflow:hidden;";
    pipWin.document.body.appendChild(root);

    pipWin.addEventListener("pagehide", () => {
      flushSync(() => {
        setIsPipOpen(false);
        setPortalRoot(null);
      });
      pipWindowRef.current = null;
    });

    setPortalRoot(root);
    setIsPipOpen(true);

    return pipWin;
  }, [cloneStyles]);

  const closePip = useCallback(() => {
    if (pipWindowRef.current && !pipWindowRef.current.closed) {
      pipWindowRef.current.close();
    }
    pipWindowRef.current = null;
    setPortalRoot(null);
    setIsPipOpen(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pipWindowRef.current && !pipWindowRef.current.closed) {
        pipWindowRef.current.close();
      }
    };
  }, []);

  return {
    isPipOpen,
    pipWindow: pipWindowRef.current,
    portalRoot,
    openPip,
    closePip,
  };
}

export function isPipSupported() {
  return typeof window !== "undefined" && "documentPictureInPicture" in window;
}
